import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { injectIntl, FormattedMessage } from 'react-intl';

import Tooltip from '../../../../components/Tooltip';

import { ReactComponent as DeliveryUNKNOWN } from './images/delivery-UNKNOWN.svg';
import { ReactComponent as DeliveryCAPRINT } from './images/delivery-CA_PRINT.svg';
import { ReactComponent as DeliveryCOALITIONEXC } from './images/delivery-COALITION_EXC.svg';
import { ReactComponent as DeliveryCOALITION } from './images/delivery-COALITION.svg';
import { ReactComponent as DeliveryCOMMONAPP } from './images/delivery-COMMONAPP.svg';
import { ReactComponent as DeliveryDOCCA } from './images/delivery-DOCCA.svg';
import { ReactComponent as DeliveryDOCCUFIDE } from './images/delivery-DOCUFIDE.svg';
import { ReactComponent as DeliveryPRINT } from './images/delivery-PRINT.svg';

import messages from './messages';

export const getDeliveryTypeIconAndMessage = (deliveryType, isApplyingTo) => {
  switch (deliveryType) {
    case 'COMMONAPP': {
      return {
        icon: <DeliveryCOMMONAPP />,
        message: isApplyingTo
          ? messages.applied.COMMONAPP
          : messages.nonApplied.COMMONAPP,
      };
    }
    case 'DOCUFIDE': {
      return {
        icon: <DeliveryDOCCUFIDE />,
        message: isApplyingTo ? messages.applied.DOCUFIDE : messages.nonApplied.DOCUFIDE,
      };
    }
    case 'PRINT': {
      return {
        icon: <DeliveryPRINT />,
        message: isApplyingTo ? messages.applied.PRINT : messages.nonApplied.PRINT,
      };
    }
    case 'DOCCA': {
      return {
        icon: <DeliveryDOCCA />,
        message: isApplyingTo ? messages.applied.DOCCA : messages.nonApplied.DOCCA,
      };
    }
    case 'CA_PRINT': {
      return {
        icon: <DeliveryCAPRINT />,
        message: isApplyingTo ? messages.applied.CA_PRINT : messages.nonApplied.CA_PRINT,
      };
    }
    case 'COALITION': {
      return {
        icon: <DeliveryCOALITION />,
        message: isApplyingTo
          ? messages.applied.COALITION
          : messages.nonApplied.COALITION,
      };
    }
    case 'COALITION_EXC': {
      return {
        icon: <DeliveryCOALITIONEXC />,
        message: isApplyingTo
          ? messages.applied.COALITION_EXC
          : messages.nonApplied.COALITION_EXC,
      };
    }
    case 'UNKNOWN': {
      return {
        icon: <DeliveryUNKNOWN />,
        message: isApplyingTo ? messages.applied.UNKNOWN : messages.nonApplied.UNKNOWN,
      };
    }
    default: {
      return {
        icon: <DeliveryUNKNOWN />,
        message: isApplyingTo ? messages.applied.UNKNOWN : messages.nonApplied.UNKNOWN,
      };
    }
  }
};

const getYesNoFromBooleanValue = value => {
  if (true === value) {
    return 'Yes';
  }
  if (false === value) {
    return 'No';
  }
  return 'Unknown';
};

const getFormattedPolicy = value => {
  if (!value || typeof value !== 'string') {
    return 'Unknown';
  }
  if (value.toLowerCase().includes('competitive ')) {
    return 'Competitive';
  }
  if (value.toLowerCase().includes('rolling ')) {
    return 'Rolling';
  }
  if (value.toLowerCase().includes('open ')) {
    return 'Open';
  }
  if (value.toLowerCase().includes('by date')) {
    return 'By Date';
  }
  if (value.toLowerCase().includes('by appointment')) {
    return 'By Appointment';
  }
  if (value.toLowerCase().includes('no ')) {
    return 'No';
  }
  return 'Unknown';
};

const ImpPoliciesTab = ({ importantPolicies, deliveryType, isApplyingTo, intl }) => {
  const admissionsPolicy = getFormattedPolicy(
    get(importantPolicies, 'admissionsPolicy.value', ''),
  );

  let notificationPolicy = get(importantPolicies, 'notificationPolicy.value', '');
  if (notificationPolicy) {
    notificationPolicy = getFormattedPolicy(notificationPolicy);
  }

  let registrationPolicy = get(importantPolicies, 'registrationPolicy.value', '');
  if (registrationPolicy) {
    registrationPolicy = getFormattedPolicy(registrationPolicy);
  }

  const earlyDecision = getYesNoFromBooleanValue(
    get(importantPolicies, 'earlyDecision.value', ''),
  );
  const waitingList = getYesNoFromBooleanValue(
    get(importantPolicies, 'waitingList.value', ''),
  );
  const testOptional = getYesNoFromBooleanValue(
    get(importantPolicies, 'testOptional.value', ''),
  );
  const commonAppMember = getYesNoFromBooleanValue(
    get(importantPolicies, 'commonAppMember.value', ''),
  );
  const coalitionAppMember = getYesNoFromBooleanValue(
    get(importantPolicies, 'coalitionAppMember.value', ''),
  );

  const tooltipRef = useRef();
  const iconMessage = getDeliveryTypeIconAndMessage(deliveryType, isApplyingTo);
  return (
    <div
      className="application-information-tab important-policies-tab-container"
      data-cy="application-information-tab"
    >
      <div className="important-policies-info">
        {iconMessage.icon}
        <div>
          <FormattedMessage {...iconMessage.message} />
          {deliveryType && deliveryType.includes('COALITION') && (
            <a
              href="http://www.coalitionforcollegeaccess.org/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Coalition Website"
            >
              <FormattedMessage {...messages.coalitionAppWebsite} />
            </a>
          )}
        </div>
      </div>
      <div className="important-policies-content" data-cy="important-policies-content">
        <div>
          <FormattedMessage {...messages.testOptional} />
          <Tooltip
            popupClassName="imp-policies-heading-tooltip"
            contentHeader={intl.formatMessage(messages.testOptional)}
            contentText={intl.formatMessage(messages.testOptionalTooltip)}
            showCloseBtn
            position="top center"
            onClose={() => {
              tooltipRef.current.focus();
            }}
          >
            <i
              className="info circle icon heading-tooltip-icon"
              tabIndex="0"
              aria-label="Tooltip"
              ref={tooltipRef}
            />
          </Tooltip>
        </div>
        <div>{testOptional}</div>
        <div>
          <FormattedMessage {...messages.commonApp} />
        </div>
        <div>{commonAppMember}</div>
        <div>
          <FormattedMessage {...messages.coalitionApp} />
        </div>
        <div>{coalitionAppMember}</div>
        <div>
          <FormattedMessage {...messages.admissionPolicy} />
        </div>
        <div>{admissionsPolicy}</div>
        {notificationPolicy && (
          <React.Fragment>
            <div>
              <FormattedMessage {...messages.notificationPolicy} />
            </div>
            <div>{notificationPolicy}</div>
          </React.Fragment>
        )}
        <div>
          <FormattedMessage {...messages.earlyDecision} />
        </div>
        <div>{earlyDecision}</div>
        <div>
          <FormattedMessage {...messages.waitList} />
        </div>
        <div>{waitingList}</div>
        {registrationPolicy && (
          <React.Fragment>
            <div>
              <FormattedMessage {...messages.registrationPolicy} />
            </div>
            <div>{registrationPolicy}</div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

ImpPoliciesTab.propTypes = {
  importantPolicies: PropTypes.object,
  deliveryType: PropTypes.string,
  isApplyingTo: PropTypes.bool,
  intl: PropTypes.object,
};

export default injectIntl(ImpPoliciesTab);
